/**
 * The glance card's HEIGHT BUDGET — how much vertical room the surface under it
 * actually has (th-glance-scroll).
 *
 * WHY THE CARD CANNOT ANSWER THIS ITSELF. A glance card composes one section per
 * enabled template, and the enabled set defaults to every registered template. Six
 * sections plus their unlock rungs, the identity header, the locked footer and the
 * Property pin run several hundred pixels — routinely taller than the map pane with
 * the TopPanel expanded, and capable of exceeding the whole map even without it.
 * No POSITION fixes that: there is no place to put a card taller than the box it
 * sits in. The card has to be bounded, and only the adapter knows by what.
 *
 * WHY AN INJECTION rather than a prop. It mirrors `GLANCE_CARD_PERSISTENCE`
 * exactly, and for the same reason: `GlanceCardCursor` wraps a SLOT, so it has no
 * prop it could thread through to the card its adopters render inside it. Provide
 * is the only channel that reaches the card on both adapters, and it keeps the
 * budget out of every intermediate component's props.
 *
 * Each adapter measures its OWN space, which is the half it is uniquely placed to
 * know — the cursor adapter the viewport it is `fixed` in, the popup adapter the
 * map container it is clipped by. A card mounted BARE injects nothing and stays
 * unbounded, exactly as it was before: no adapter, no clipping surface, no cap.
 *
 * The provided value is a ref/computed of PIXELS, or null for "unbounded".
 */

export const GLANCE_CARD_HEIGHT_BUDGET = Symbol('glanceCardHeightBudget')

/**
 * Breathing room between the card and the edge of whatever contains it, applied
 * at BOTH edges. Single-sourced so the popup adapter's container inset and the
 * cursor adapter's viewport inset cannot drift apart — a card that fits one
 * surface's arithmetic and not the other's is the bug this constant prevents.
 */
export const GLANCE_CARD_EDGE_PADDING = 12

/**
 * The usable height inside a container of `available` px. Returns null for a
 * non-positive or unmeasured container, which reads as "unbounded" — a card is
 * better momentarily too tall than collapsed to nothing by a 0-height measurement
 * taken before layout settles.
 */
export function heightBudgetFor(available) {
  if (!Number.isFinite(available) || available <= 0) return null
  const budget = available - GLANCE_CARD_EDGE_PADDING * 2
  return budget > 0 ? budget : null
}
